import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Pin, PinOff, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtNum } from "@/lib/studio";

export const toggleFeatured = async (sid, featured, qc) => {
  try {
    await api.put(`/admin/featured/${sid}`, { featured });
    toast.success(featured ? "Pinned to Featured" : "Removed from Featured");
    ["admin-featured", "library-featured", "library"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  } catch (e) { toast.error(errMsg(e)); }
};

const Row = ({ s, idx, count, onMove, onToggle }) => (
  <div className={`flex flex-wrap items-center gap-3 rounded-md border p-3 ${s.featured_rank != null ? "border-[#D97706]/60 bg-[#1A1508]" : "border-[#2D3442] bg-[#0F131A]"}`} data-testid={`featured-row-${s.id}`}>
    <div className="min-w-0 flex-1">
      <div className="font-display text-lg text-[#F3EBDD] [overflow-wrap:anywhere]">{s.title}</div>
      <div className="text-xs text-[#A8A090]">{s.universe_name} · {s.author || s.owner_email} · {s.chapters} chapters · {fmtNum(s.words)} words
        {s.rating && <span className="ml-2 inline-flex items-center gap-1 text-[#FBBF24]"><Star className="h-3 w-3 fill-current" />{s.rating}</span>}</div>
    </div>
    {s.featured_rank != null && <>
      <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => onMove(idx, -1)} data-testid={`featured-up-${s.id}`}><ArrowUp className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" disabled={idx === count - 1} onClick={() => onMove(idx, 1)} data-testid={`featured-down-${s.id}`}><ArrowDown className="h-4 w-4" /></Button>
    </>}
    <Button size="sm" variant={s.featured_rank != null ? "outline" : "default"} onClick={onToggle} data-testid={`featured-toggle-${s.id}`}>
      {s.featured_rank != null ? <><PinOff className="mr-1 h-3.5 w-3.5" />Unpin</> : <><Pin className="mr-1 h-3.5 w-3.5" />Pin</>}
    </Button>
  </div>
);

export const FeaturedPanel = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-featured"], queryFn: () => api.get("/admin/featured").then((r) => r.data) });
  if (!data) return null;
  const pinned = data.stories.filter((s) => s.featured_rank != null);
  const rest = data.stories.filter((s) => s.featured_rank == null);
  const move = async (i, d) => {
    const ids = pinned.map((s) => s.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try { await api.put("/admin/featured-order", { ids }); qc.invalidateQueries({ queryKey: ["admin-featured"] }); qc.invalidateQueries({ queryKey: ["library-featured"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-6" data-testid="admin-featured">
      <div>
        <div className="lf-label mb-3" data-testid="featured-count">Featured on More Universes ({pinned.length}/{data.max})</div>
        {pinned.length === 0 && <p className="text-sm text-[#A8A090]" data-testid="featured-empty">No stories pinned yet.</p>}
        <div className="space-y-2">{pinned.map((s, i) => <Row key={s.id} s={s} idx={i} count={pinned.length} onMove={move} onToggle={() => toggleFeatured(s.id, false, qc)} />)}</div>
      </div>
      <div>
        <div className="lf-label mb-3">Other listed stories ({rest.length})</div>
        {rest.length === 0 && <p className="text-sm text-[#A8A090]">No other stories are listed on More Universes.</p>}
        <div className="space-y-2">{rest.map((s) => <Row key={s.id} s={s} onToggle={() => toggleFeatured(s.id, true, qc)} />)}</div>
      </div>
    </section>
  );
};
